import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import {
  useQuery,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";

/**
 * Ausgewählte `user_private`-Spalten, die beim Dashboard-Boot bisher als 6–7
 * EINZELNE Supabase-Requests (je mit eigenem CORS-Preflight) abgefragt wurden
 * (`role`, `settings`, `community_id`, `creator_code`, `store_beta_contact_email`,
 * `first_name`/`last_name`, `mentor_user_ids_by_dance`). Diese Row wird jetzt in
 * EINEM Request geladen; einzelne Hooks (`useUserPrivateSettings`,
 * `useStoreBetaContact`, `useUserCommunity`, `useDashboardProfile`,
 * `useMentorProfiles`) lesen daraus statt eigene Requests zu feuern.
 */
export type UserPrivateProfileRow = {
  settings: Record<string, unknown>;
  role: "admin" | "terminal" | "user" | "contributor" | null;
  community_id: string | null;
  creator_code: string | null;
  store_beta_contact_email: string | null;
  first_name: string | null;
  last_name: string | null;
  mentor_user_ids_by_dance: unknown;
};

export function userPrivateProfileQueryKey(
  userId: string | null | undefined
): QueryKey {
  return ["user_private_profile", userId] as const;
}

export async function fetchUserPrivateProfile(
  userId: string
): Promise<UserPrivateProfileRow> {
  const { data, error } = await supabase
    .from("user_private")
    .select(
      "settings, role, community_id, creator_code, store_beta_contact_email, first_name, last_name, mentor_user_ids_by_dance"
    )
    .eq("user_id", userId)
    .maybeSingle();

  if (error && error.code !== "PGRST116") {
    throw new Error(`Fehler beim Laden von user_private: ${error.message}`);
  }

  const rawSettings = data?.settings;
  const settings =
    rawSettings && typeof rawSettings === "object" && !Array.isArray(rawSettings)
      ? (rawSettings as Record<string, unknown>)
      : {};

  return {
    settings,
    role: (data?.role as UserPrivateProfileRow["role"]) ?? null,
    community_id: data?.community_id ?? null,
    creator_code: data?.creator_code ?? null,
    store_beta_contact_email: data?.store_beta_contact_email ?? null,
    first_name: data?.first_name ?? null,
    last_name: data?.last_name ?? null,
    mentor_user_ids_by_dance: data?.mentor_user_ids_by_dance ?? null,
  };
}

const PROFILE_STALE_TIME_MS = 5 * 60 * 1000;
const PROFILE_GC_TIME_MS = 10 * 60 * 1000;

/**
 * Lädt die gemeinsame `user_private`-Zeile über den React-Query-Cache
 * (dedupliziert parallele Aufrufe automatisch — `fetchQuery` mit demselben
 * Key liefert dieselbe In-Flight-Promise). Für Nutzung AUSSERHALB von
 * React-Komponenten/Hooks (z. B. innerhalb anderer `queryFn`s).
 */
export function getUserPrivateProfile(
  queryClient: QueryClient,
  userId: string
): Promise<UserPrivateProfileRow> {
  return queryClient.fetchQuery({
    queryKey: userPrivateProfileQueryKey(userId),
    queryFn: () => fetchUserPrivateProfile(userId),
    staleTime: PROFILE_STALE_TIME_MS,
  });
}

/**
 * React-Query für die gemeinsame `user_private`-Zeile des aktuellen Nutzers.
 */
export function useUserPrivateProfile(userId?: string | null) {
  const { user, authReady } = useAuth();
  const resolvedUserId = userId ?? user?.id ?? null;

  return useQuery({
    queryKey: userPrivateProfileQueryKey(resolvedUserId),
    queryFn: () => fetchUserPrivateProfile(resolvedUserId!),
    enabled: authReady && !!resolvedUserId,
    staleTime: PROFILE_STALE_TIME_MS,
    gcTime: PROFILE_GC_TIME_MS,
  });
}

export function invalidateUserPrivateProfile(
  queryClient: QueryClient,
  userId: string | null | undefined
): void {
  queryClient.invalidateQueries({
    queryKey: userPrivateProfileQueryKey(userId),
  });
}
